import { useState } from 'react';
import { Mail, Phone, MapPin, Instagram, Facebook } from 'lucide-react';

const Footer = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  return (
    <footer className="bg-prose-black text-white">
      <div className="border-y border-white/10 px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 items-center">
          <div>
            <h3 className="text-lg sm:text-2xl font-display font-bold mb-2">Stay Updated</h3>
            <p className="text-gray-400 text-xs sm:text-sm">Get the latest property management tips and market insights</p>
          </div>
          <form action="https://formsubmit.co/info@project-prose.com" method="POST" onSubmit={() => setIsSubmitting(true)} className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <input 
              type="email"
              name="email"
              placeholder="Enter your email"
              required
              className="flex-1 px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-prose-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white text-xs sm:text-sm"
            />
            <button type="submit" disabled={isSubmitting} className="bg-prose-gold text-prose-black px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold hover:bg-prose-gold/90 transition-all whitespace-nowrap text-xs sm:text-sm md:text-base disabled:opacity-70 disabled:cursor-not-allowed">
              {isSubmitting ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
        </div>
      </div>
    </div>

    <div className="px-4 sm:px-6 py-8 sm:py-16 pb-16">
      <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 sm:gap-8 mb-8 sm:mb-12">
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <img 
              src="/images/logos/Full white no bg.png" 
              alt="ProjectProse" 
              className="w-8 sm:w-10 h-8 sm:h-10"
              loading="lazy"
            />
            <h3 className="text-base sm:text-2xl font-display font-bold text-white truncate">ProjectProse</h3>
          </div>
          <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">Redefining property management through professionalism and a personal touch.</p>
          <div className="flex gap-4 pt-4">
            <a href="https://www.instagram.com/projectppm" target="_blank" rel="noopener noreferrer" className="text-white hover:text-prose-gold transition-colors">
              <Instagram size={20} />
            </a>
            <a href="https://web.facebook.com/people/Project-Prose-Properties-Management/61569140147934/" target="_blank" rel="noopener noreferrer" className="text-white hover:text-prose-gold transition-colors">
              <Facebook size={20} />
            </a>
            <a href="https://www.tiktok.com/@projectprose.prop" target="_blank" rel="noopener noreferrer" className="text-white hover:text-prose-gold transition-colors">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.1 1.82 2.89 2.89 0 0 1 2.31-4.64 2.86 2.86 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-.54-.05z"/>
              </svg>
            </a>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="text-xs sm:text-sm uppercase tracking-widest text-white font-bold pb-2 border-b-2 border-prose-gold">Services</h4>
          <ul className="space-y-2 text-xs sm:text-sm">
            <li><a href="/services" className="text-gray-400 hover:text-white transition-colors">Guaranteed Rent</a></li>
            <li><a href="/services" className="text-gray-400 hover:text-white transition-colors">Tenant Sourcing</a></li>
            <li><a href="/services" className="text-gray-400 hover:text-white transition-colors">Maintenance</a></li>
            <li><a href="/services" className="text-gray-400 hover:text-white transition-colors">Compliance</a></li>
          </ul>
        </div>

        <div className="space-y-4">
          <h4 className="text-xs sm:text-sm uppercase tracking-widest text-white font-bold pb-2 border-b-2 border-prose-gold">Company</h4>
          <ul className="space-y-2 text-xs sm:text-sm">
            <li><a href="/about" className="text-gray-400 hover:text-white transition-colors">About Us</a></li>
            <li><a href="/services" className="text-gray-400 hover:text-white transition-colors">Services</a></li>
            <li><a href="/properties" className="text-gray-400 hover:text-white transition-colors">Properties</a></li>
            <li><a href="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</a></li>
          </ul>
        </div>

        <div className="space-y-4">
          <h4 className="text-xs sm:text-sm uppercase tracking-widest text-white font-bold pb-2 border-b-2 border-prose-gold">Legal</h4>
          <ul className="space-y-2 text-xs sm:text-sm">
            <li><a href="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</a></li>
          </ul>
        </div>

        <div className="space-y-4">
          <h4 className="text-xs sm:text-sm uppercase tracking-widest text-white font-bold pb-2 border-b-2 border-prose-gold">Contact</h4>
          <ul className="space-y-3 text-xs sm:text-sm">
            <li className="flex items-start gap-3">
              <Phone size={16} className="text-white flex-shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1">
                <a href="tel:+441470275001" className="text-gray-400 hover:text-white transition-colors">+44 1470275001</a>
                <a href="tel:+447478036678" className="text-gray-400 hover:text-white transition-colors">+44 7478036678</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={16} className="text-white flex-shrink-0 mt-0.5" />
              <a href="mailto:info@project-prose.com" className="text-gray-400 hover:text-white transition-colors break-all">info@project-prose.com</a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={16} className="text-white flex-shrink-0 mt-0.5" />
              <span className="text-gray-400">London, UK</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10"></div>

      <div className="max-w-7xl mx-auto pt-6 sm:pt-8 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 items-center text-center md:text-left text-xs sm:text-sm">
        <div className="text-gray-500 uppercase tracking-[0.2em]">
          © 2024 Project Prose
        </div>
        <div className="text-center md:text-right text-gray-500">
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="hover:text-white transition-colors">Back to Top ↑</button>
        </div>
      </div>
    </div>
    </footer>
  );
};

export default Footer;